import { useCallback, useEffect, useRef, useState } from 'react';
import { usePendingWriteNavigation } from '../inventory/usePoReceiptWrite';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PriceBookUpdateInput } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import {
  refusalReason,
  refusalStatus,
  isRetryableMutationRefusal,
  isStockBusy,
} from '../../lib/refusal';
import { useSession } from '../session';
export type PriceBookEditorItem = {
  id: string;
  kind: string;
  name: string;
  description: string | null;
  unitLabel: string | null;
  unitPriceCents: number;
  taxable: boolean;
  category: string | null;
};
type Attempt = {
  input: Readonly<PriceBookUpdateInput>;
  item: Readonly<PriceBookEditorItem>;
  phase: 'writing' | 'retry' | 'review' | 'reconcile' | 'refreshing' | 'read_failed';
  accepted: boolean;
  uncertain: boolean;
  running: boolean;
  error?: string;
  acceptedKind?: string;
};
/** One ordinary edit, owned above account-keyed/dismissible catalogue children. */
export function usePriceBookWrite() {
  const session = useSession(),
    owner = `${session.orgId}:${session.userId}:${session.role}`,
    allowed = session.role === 'owner' || session.role === 'office';
  const current = useRef(owner),
    generation = useRef(0),
    alive = useRef(true),
    records = useRef(new Map<string, Attempt>()),
    keys = useRef(createIntentKey());
  if (current.current !== owner) {
    current.current = owner;
    generation.current++;
  }
  const [, tick] = useState(0),
    [savedOwner, setSavedOwner] = useState<string>();
  const qc = useQueryClient(),
    trpc = useTRPC(),
    mutation = useMutation(
      trpc.priceBook.update.mutationOptions({ retry: false, networkMode: 'always' }),
    );
  const notify = () => {
    if (alive.current && current.current === owner) tick((v) => v + 1);
  };
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const hasPending = useCallback(() => records.current.size > 0, []);
  const shouldBlock = useCallback(
    () =>
      hasPending() &&
      !window.confirm(
        'This catalogue edit is unresolved. Leaving ends this session-only recovery. Leave anyway?',
      ),
    [hasPending],
  );
  usePendingWriteNavigation(hasPending, shouldBlock);
  const refresh = async (review = false) => {
    const a = records.current.get(owner);
    if (
      !a ||
      a.running ||
      !allowed ||
      current.current !== owner ||
      !alive.current ||
      (review ? a.phase !== 'review' : !a.accepted)
    )
      return false;
    const gen = generation.current,
      owns = () =>
        alive.current && current.current === owner && generation.current === gen;
    a.running = true;
    a.phase = 'refreshing';
    notify();
    try {
      await Promise.all([
        qc.cancelQueries({ queryKey: trpc.priceBook.list.pathKey() }),
        qc.cancelQueries({ queryKey: trpc.pricing.pathKey() }),
      ]);
      if (!owns()) return false;
      await Promise.all([
        qc.fetchQuery({
          ...trpc.priceBook.list.queryOptions(),
          staleTime: 0,
          retry: false,
        }),
        qc.fetchQuery({
          ...trpc.pricing.current.queryOptions({}),
          staleTime: 0,
          retry: false,
        }),
        qc.fetchQuery({
          ...trpc.pricing.history.queryOptions({ itemId: a.input.itemId }),
          staleTime: 0,
          retry: false,
        }),
        qc.fetchQuery({
          ...trpc.pricing.raisedCount.queryOptions(),
          staleTime: 0,
          retry: false,
        }),
        ...((a.acceptedKind ?? a.item.kind) === 'material'
          ? [
              qc.fetchQuery({
                ...trpc.pricing.itemPolicy.queryOptions({ itemId: a.input.itemId }),
                staleTime: 0,
                retry: false,
              }),
            ]
          : []),
      ]);
      if (!owns()) return false;
      await qc.invalidateQueries(
        { queryKey: trpc.pricing.pathKey() },
        { throwOnError: true },
      );
      if (!owns()) return false;
      records.current.delete(owner);
      keys.current.clear();
      if (!review) setSavedOwner(owner);
      notify();
      return true;
    } catch (error) {
      a.error = refusalReason(error);
      return false;
    } finally {
      a.running = false;
      if (records.current.get(owner) === a) {
        a.phase = a.accepted ? 'read_failed' : 'review';
        notify();
      }
    }
  };
  const send = async () => {
    const a = records.current.get(owner);
    if (
      !a ||
      a.running ||
      a.accepted ||
      !allowed ||
      !alive.current ||
      current.current !== owner
    )
      return;
    const gen = generation.current;
    a.running = true;
    a.phase = 'writing';
    notify();
    try {
      const result = await mutation.mutateAsync(a.input);
      a.accepted = true;
      a.acceptedKind = result.kind;
      a.phase = 'read_failed';
    } catch (error) {
      const status = refusalStatus(error);
      if (status === 0 || status === 401 || status === 408 || status >= 500)
        a.uncertain = true;
      a.phase =
        status === 409 && !isStockBusy(error)
          ? 'reconcile'
          : (a.uncertain && status === 403) || isRetryableMutationRefusal(error)
            ? 'retry'
            : 'review';
      a.error = refusalReason(error);
      return;
    } finally {
      a.running = false;
      notify();
    }
    if (alive.current && current.current === owner && generation.current === gen)
      await refresh();
  };
  const begin = (
    input: Omit<PriceBookUpdateInput, 'clientId' | 'mutationId'>,
    item: PriceBookEditorItem,
  ) => {
    if (
      !allowed ||
      records.current.has(owner) ||
      !alive.current ||
      current.current !== owner
    )
      return;
    const frozen = Object.freeze({
      ...JSON.parse(JSON.stringify(input)),
      ...keys.current.forIntent([owner, input]),
    }) as Readonly<PriceBookUpdateInput>;
    records.current.set(owner, {
      input: frozen,
      item: Object.freeze({ ...item }),
      phase: 'writing',
      accepted: false,
      uncertain: false,
      running: false,
    });
    setSavedOwner(undefined);
    notify();
    void send();
  };
  return {
    attempt: records.current.get(owner),
    locked: records.current.has(owner),
    saved: savedOwner === owner && !records.current.has(owner),
    begin,
    retry: () => void send(),
    refresh: () => refresh(),
    review: () => refresh(true),
  };
}
export type PriceBookWrite = ReturnType<typeof usePriceBookWrite>;
